import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/format';
import { getAnalysis, exportAnalysisExcel } from '../api/analysis';
import { getFilterOptions } from '../api/dashboard';
import SearchableSelect from '../components/SearchableSelect';
import SendGpReportModal from '../components/SendGpReportModal';

const TABS = [
  { id: 'salesman', label: 'Salesmen' },
  { id: 'category', label: 'Categories' },
  { id: 'customer', label: 'Customers' },
  { id: 'product', label: 'Products' },
  { id: 'channel', label: 'Channels' },
];

const BADGE = {
  'Target Gap': 'bg-red-100 text-red-800',
  Rate: 'bg-amber-100 text-amber-800',
  Mix: 'bg-purple-100 text-purple-800',
  Volume: 'bg-blue-100 text-blue-800',
  'Below Cost': 'bg-red-200 text-red-900',
  Reference: 'bg-green-100 text-green-800',
};

const pct = (v, dp = 1) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(dp)}%`);
const money = (v) => (v === null || v === undefined ? '—' : formatCurrency(v));
const signedPts = (v) => (v === null || v === undefined ? '—' : `${v > 0 ? '+' : ''}${Number(v).toFixed(1)} pts`);
const signedMoney = (v) => (v === null || v === undefined ? '—' : `${v > 0 ? '+' : v < 0 ? '−' : ''}${formatCurrency(Math.abs(v))}`);
const tone = (v) => (v === null || v === undefined || v === 0 ? 'text-gray-700' : v > 0 ? 'text-green-700' : 'text-red-700');

function defaultMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function Stat({ label, value, sub, valueClass = 'text-gray-800' }) {
  return (
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`text-xl font-semibold tabular-nums ${valueClass}`}>{value}</div>
      {sub && <div className="text-xs text-gray-500">{sub}</div>}
    </div>
  );
}

export default function GpAnalysis() {
  const { canViewGP } = useAuth();
  const [month, setMonth] = useState(defaultMonth());
  const [channel, setChannel] = useState(''); // '' = All Channels
  const [channelItems, setChannelItems] = useState([]);
  const [tab, setTab] = useState('salesman');
  const [search, setSearch] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEmail, setShowEmail] = useState(false);
  const [exporting, setExporting] = useState(false);

  async function handleExport() {
    setExporting(true);
    setError('');
    try {
      await exportAnalysisExcel({ month, dimension: tab, channel, search });
    } catch (e) {
      setError('Could not export the analysis');
    } finally {
      setExporting(false);
    }
  }

  useEffect(() => {
    if (!canViewGP) return;
    getFilterOptions()
      .then((opts) => setChannelItems((opts.channels || []).map((c) => ({ code: c, name: c }))))
      .catch(() => setChannelItems([]));
  }, [canViewGP]);

  useEffect(() => {
    if (!canViewGP) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    getAnalysis(month, tab, channel)
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(e.response?.data?.message || 'Could not load the analysis'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [month, tab, channel, canViewGP]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = data?.rows || [];
    return q ? list.filter((r) => `${r.name} ${r.key}`.toLowerCase().includes(q)) : list;
  }, [data, search]);

  if (!canViewGP) {
    return (
      <div>
        <h1 className="text-xl font-semibold text-gray-800 mb-2">GP Analysis</h1>
        <p className="text-sm text-gray-600">GP performance data needs the approve action on this module.</p>
      </div>
    );
  }

  const o = data?.overall;
  const pace = data?.pace;
  const targeted = data?.dimensionHasTargets && data?.hasTargets;
  // Salesman/category targets cover every channel, so the API leaves them off while a
  // channel is selected rather than compare one channel's sales with an all-channel target.
  const targetsHiddenByChannel = !!data?.channel && (data.dimension === 'salesman' || data.dimension === 'category');
  const progress = o?.progressPercent != null ? Math.min(100, Math.max(0, o.progressPercent)) : null;
  const elapsedPct = pace ? (pace.daysElapsed / pace.daysInMonth) * 100 : 0;

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-800">GP Analysis</h1>
          <p className="text-sm text-gray-600">Actual GP against the saved targets — where it is being lost and what to focus on.</p>
        </div>
        <div className="flex items-end gap-2">
          <SearchableSelect label="Channel" value={channel} onChange={setChannel} items={channelItems} />
          <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="border border-gray-300 rounded px-3 py-1.5 text-sm" />
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting || loading}
            title="All five tabs for this month and channel; the search narrows the current tab's sheet"
            className="px-3 py-1.5 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-50"
          >
            {exporting ? 'Exporting…' : 'Export Excel'}
          </button>
          <button type="button" onClick={() => setShowEmail(true)} className="px-3 py-1.5 text-sm rounded bg-blue-600 hover:bg-blue-700 text-white">
            Email Report
          </button>
        </div>
      </div>
      {showEmail && <SendGpReportModal month={month} onClose={() => setShowEmail(false)} />}

      {error && <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}

      {o && (
        <section className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex flex-wrap justify-between gap-2 mb-4 text-sm text-gray-600">
            <span>
              GP PERFORMANCE · <span className="font-medium text-gray-800">{month}</span>
              {data.channel && (
                <>
                  {' '}· <span className="font-medium text-gray-800">{data.channel}</span> only
                </>
              )}
            </span>
            <span>
              {pace.dataThrough ? `Data through ${pace.dataThrough}` : 'No sales uploaded for this month'} · {pace.daysElapsed} days elapsed · {pace.daysRemaining} remaining
            </span>
          </div>

          {data.channel && data.hasTargets && (
            <div className="mb-4 text-xs text-gray-500">Target figures here are the {data.channel} channel target, not the company target.</div>
          )}

          {!data.hasTargets && (
            <div className="mb-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              No targets saved for this month. <Link to="/gp-report/gp-targets" className="underline">Set them on GP Targets</Link> to see gaps and pace.
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Actual GP%" value={pct(o.gpPercent, 2)} />
            <Stat label="Target GP%" value={pct(o.targetGpPercent, 2)} />
            <Stat label="Gap" value={signedPts(o.gpGapPercent)} valueClass={tone(o.gpGapPercent)} />
            <Stat label="Required GP / day" value={money(o.requiredGpPerDay)} sub={o.gpRemaining != null ? `QAR ${money(o.gpRemaining)} still required` : ''} />
            <Stat label="Actual GP (QAR)" value={money(o.gpAmount)} />
            <Stat label="Target GP (QAR)" value={money(o.targetGpAmount)} />
            <Stat label="GP gap (QAR)" value={signedMoney(o.gpGapAmount)} valueClass={tone(o.gpGapAmount)} />
            <Stat label="Normal GP% (3M)" value={pct(o.baselineGpPercent, 2)} sub={o.baselineMethod ? `target method: ${o.baselineMethod}` : ''} />
          </div>

          {progress !== null && (
            <div className="mt-4">
              <div className="relative h-3 bg-gray-100 rounded overflow-hidden">
                <div className="absolute inset-y-0 left-0 bg-blue-500" style={{ width: `${progress}%` }} />
                <div className="absolute inset-y-0 w-0.5 bg-gray-700" style={{ left: `${elapsedPct}%` }} title="Share of the month elapsed" />
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>{progress.toFixed(0)}% of target GP achieved</span>
                <span>marker = {elapsedPct.toFixed(0)}% of month elapsed</span>
              </div>
            </div>
          )}
        </section>
      )}

      {data?.focusNext?.length > 0 && (
        <section className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <h2 className="text-sm font-semibold text-gray-800 mb-3">Focus next</h2>
          <ol className="space-y-2">
            {data.focusNext.map((f, i) => (
              <li key={i} className="flex gap-3 text-sm">
                <span className="text-gray-400 w-4 shrink-0">{i + 1}.</span>
                <span className={`shrink-0 self-start text-xs px-2 py-0.5 rounded ${BADGE[f.type] || 'bg-gray-100 text-gray-700'}`}>{f.type}</span>
                <span>
                  <span className="font-medium text-gray-800">{f.title}</span> <span className="text-gray-600">— {f.detail}</span>
                </span>
              </li>
            ))}
          </ol>
        </section>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => {
              setTab(t.id);
              setSearch('');
            }}
            className={`px-3 py-1.5 text-sm rounded ${tab === t.id ? 'bg-gray-800 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
          >
            {t.label}
          </button>
        ))}
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="ml-auto border border-gray-300 rounded px-3 py-1.5 text-sm" />
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-right">Revenue</th>
              <th className="px-3 py-2 text-right">GP</th>
              <th className="px-3 py-2 text-right">GP%</th>
              <th className="px-3 py-2 text-right">{targeted ? 'Target GP%' : 'Normal GP% (3M)'}</th>
              <th className="px-3 py-2 text-right">{targeted ? 'Gap' : 'vs normal'}</th>
              {targeted && <th className="px-3 py-2 text-right">Target GP</th>}
              <th className="px-3 py-2 text-right" title={targeted ? 'Target GP earned so far minus actual GP' : 'GP lost against its own normal margin'}>
                GP shortfall
              </th>
              <th className="px-3 py-2 text-right">vs last month</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-gray-500">Loading…</td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-gray-500">No rows.</td>
              </tr>
            )}
            {!loading &&
              rows.map((r) => (
                <tr key={r.key} className="border-t border-gray-100">
                  <td className="px-3 py-2">
                    {r.name}
                    {r.belowCost && <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-800">Below cost</span>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(r.revenue)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(r.gpAmount)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{pct(r.gpPercent)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{targeted ? pct(r.targetGpPercent) : pct(r.baselineGpPercent)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${tone(targeted ? r.gpGapPercent : r.changeVsBaseline)}`}>
                    {signedPts(targeted ? r.gpGapPercent : r.changeVsBaseline)}
                  </td>
                  {targeted && <td className="px-3 py-2 text-right tabular-nums">{money(r.targetGpAmount)}</td>}
                  <td className={`px-3 py-2 text-right tabular-nums ${r.impact > 0 ? 'text-red-700 font-medium' : 'text-gray-500'}`}>{r.impact > 0 ? money(r.impact) : '—'}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${tone(r.changeVsPreviousMonth)}`}>{signedPts(r.changeVsPreviousMonth)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-gray-500">
        Sorted by GP shortfall (money), not by percentage gap.{' '}
        {targeted
          ? 'Shortfall = target GP earned so far at this point of the month minus actual GP.'
          : targetsHiddenByChannel
            ? `Salesman and category targets cover all channels, so they are not compared with ${data.channel} sales alone — rows are compared with their own 3-month normal margin in ${data.channel}.`
            : 'Customers, products and sub-channels have no targets — they are compared with their own 3-month normal margin.'}
      </p>
    </div>
  );
}
